import { useScrollPosition } from '@n8tb1t/use-scroll-position';
import React, { useEffect, useState } from 'react';
import DocumentTitle from 'react-document-title';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { Dimmer, Loader, Modal } from 'semantic-ui-react';
import {
    Audit,
    AuditChecklistHeader,
    AuditCheckpoint,
    AuditCustomFieldDocumentValue,
    AuditCustomFieldSupplierDocumentValue,
    AuditCustomFieldValue,
    AuditStatus,
    FileResource,
    MultiLingualText,
    SetCustomFieldSupplierDocuments,
    SetFieldSupplierDocumentValue,
    SupplierDocument
} from '../../../../backend_api_2';
import ToTop from '../../../../base/components/ToTop';
import Button from '../../../../base/components/basic/Button';
import Icon from '../../../../base/components/basic/Icon';
import {
    getLocaleLanguageString,
    isAndroidOS,
    isMobileDevice,
    twMerge,
} from '../../../../base/utils';
import { useAppDispatch } from '../../../../store';
import history from '../../../../store/history';
import { TEMPORARY_USER_MODE } from '../../../authentication/authenticationSlice';
import PageContainer from '../../../globals/components/PageContainer';
import { getAuditContentByIdSelector, getAuditsLoadingSelector, getFetchingAuditErrorCodeFailedSelector, getPreviousAuditContentByIdSelector } from '../../selectors/auditSelectors';
import { getAuditContentById, resetAuditWithContent, updateCheckpointComment, updateProductionUnitCustomFieldCheckpoint, updateSupplierCustomFieldCheckpoint } from '../../slices/auditSlice';
import { DefaultCheckpoint } from './DefaultCheckpoint';
import { MultipleChoiceCheckpoint } from './MultipleChoiceCheckpoint';
import { ProductionUnitCustomFieldCheckpoint, SupplierCustomFieldCheckpoint } from './ProductionUnitCustomFieldCheckpoint';
import { SummaryAndSubmit } from './SummaryAndSubmitAudit';
import { TextInputsCheckpoint } from './TextInputsCheckpoint';
import { TextTableCheckpoint } from './TextTableCheckpoint';
import { WebAuditInfo } from './WebAuditInfo';
import { doesAuditHaveAnyMandatoryCheckpoints, doesChecklistHeaderHaveAnyMandatoryCheckpoints, doesCheckpointRequiresInputOrMedia, isDocumentValid } from './mandatory';
import { CheckpointHeaderProgressPopup } from './CheckpointHeaderProgressPopup';
import { AddMediaModal } from './AuditCheckpointMedia';
import { MeasurementTableCheckpoint } from './MeasurementTableCheckpoint';
import { TextArea, TextAreaHeight } from '../../../../base/ui/components/TextArea';
import ContextMenu, { ContextMenuActionItemType } from '../../../../base/ui/components/contextMenu/ContextMenu';
import AuditLockedModal from './AuditLockedModal';
import NoAuditAccessPage from './NoAuditAccessPage';
import { getProductionUnitDocumentTypeGroups } from '../../../productionUnits/slice/productionUnitsSlice';
import { getSupplierDocumentTypeGroups } from '../../../suppliers/slice/suppliersSlice';
import SupplierDocumentViewer, { DocumentViewerAction } from '../../../../base/ui/components/documentViewer/SupplierDocumentViewer';
import { getViewingSupplierDocumentSelector, supplierDocumentsSliceActions } from '../../../supplierDocuments/supplierDocumentsSlice';

type PerformAuditPageProps = {
    auditId: string;
};

// Extended type for supplier document viewer state


const PerformAuditPage = (props: PerformAuditPageProps) => {
    const { auditId } = props;
    const intl = useIntl();
    const dispatch = useAppDispatch();
    const auditWithContent = useSelector(getAuditContentByIdSelector);
    const previousAudit = useSelector(getPreviousAuditContentByIdSelector);
    const documentation = useSelector(getViewingSupplierDocumentSelector)

    const showContinueInBrowserGuide = auditWithContent?.audit?.locked && previousAudit?.audit?.locked === false;

    const [showLockedAuditModal, setShowLockedAuditModal] = useState(false);
    const [showRequireInputModal, setShowRequireInputModal] = useState(false);
    const hasMandatoryInput = doesAuditHaveAnyMandatoryCheckpoints(auditWithContent?.checklist);
    const [triedToSubmit, setTriedToSubmit] = React.useState(false);
    const isTemporaryUserMode = sessionStorage.getItem(TEMPORARY_USER_MODE);
    const isLoading = useSelector(getAuditsLoadingSelector);
    const errorCodeFetchingAudit = useSelector(getFetchingAuditErrorCodeFailedSelector);

    // function openInApp() {
    //     window.location.href = getAppLink({ auditId });
    // }
    
    // Create document viewer actions
    const documentActions: DocumentViewerAction = {
        updateValidity: (startDate: string, expirationDate: string) => {
            console.log("updateValidity", startDate, expirationDate);
            if (!documentation) return;
            console.log("documentation", documentation);

            // Find the relevant checkpoint and custom field
            const fileResourceId = documentation.media_resources?.[0]?.id;
            if (!fileResourceId) return;

            // Search through all checkpoints to find the one with the matching file resource
            let checkpointWithDocument: AuditCheckpoint | null = null;
            let customFieldWithDocument: AuditCustomFieldValue | null = null;
            let isProductionUnitField = false;

            auditWithContent.checklist.headers.forEach(header => {
                header.checkpoints.forEach(checkpoint => {
                    // Check production unit custom fields
                    if (checkpoint.checkpoint_type === AuditCheckpoint.checkpoint_type.PRODUCTION_UNIT_CUSTOM_FIELDS) {
                        checkpoint.production_unit_custom_fields?.forEach(customField => {
                            if (customField.value.type === AuditCustomFieldSupplierDocumentValue.type.SUPPLIER_DOCUMENT) {
                                const supplierDocValue = customField.value as AuditCustomFieldSupplierDocumentValue;
                                const foundInNew = supplierDocValue.new_documents.some(doc => doc.file_resource?.id === fileResourceId);
                                if (foundInNew) {
                                    checkpointWithDocument = checkpoint;
                                    customFieldWithDocument = customField;
                                    isProductionUnitField = true;
                                }
                            }
                        });
                    }

                    // Check supplier custom fields
                    if (checkpoint.checkpoint_type === AuditCheckpoint.checkpoint_type.SUPPLIER_CUSTOM_FIELDS) {
                        checkpoint.supplier_custom_fields?.forEach(customField => {
                            if (customField.value.type === AuditCustomFieldSupplierDocumentValue.type.SUPPLIER_DOCUMENT) {
                                const supplierDocValue = customField.value as AuditCustomFieldSupplierDocumentValue;
                                const foundInNew = supplierDocValue.new_documents.some(doc => doc.file_resource?.id === fileResourceId);
                                if (foundInNew) {
                                    checkpointWithDocument = checkpoint;
                                    customFieldWithDocument = customField;
                                    isProductionUnitField = false;
                                }
                            }
                        });
                    }
                });
            });

            if (!checkpointWithDocument || !customFieldWithDocument) return;

            const customFieldValue = customFieldWithDocument.value as AuditCustomFieldSupplierDocumentValue;
            const newDocuments = customFieldValue.new_documents;

            const updatedDocuments = newDocuments.map(doc => {
                if (doc.file_resource?.id === fileResourceId) {
                    return {
                        ...doc,
                        start_date: startDate,
                        expiration_date: expirationDate
                    };
                }
                return doc;
            });

            // Dispatch the appropriate update action
            if (isProductionUnitField) {
                dispatch(updateProductionUnitCustomFieldCheckpoint({
                    audit_id: auditWithContent.audit.id,
                    checkpoint_id: checkpointWithDocument.id,
                    custom_field_id: customFieldWithDocument.id,
                    new_value: {
                        type: SetFieldSupplierDocumentValue.type.SUPPLIER_DOCUMENT,
                        new_value: {
                            type: SetCustomFieldSupplierDocuments.type.SUPPLIER_DOCUMENTS,
                            supplier_documents: updatedDocuments.map(doc => ({
                                start_date: doc.start_date || '',
                                expiration_date: doc.expiration_date || '',
                                file_resource_id: doc.file_resource?.id,
                                status: doc.status
                            }))
                        }
                    }
                }));
            } else {
                dispatch(updateSupplierCustomFieldCheckpoint({
                    audit_id: auditWithContent.audit.id,
                    checkpoint_id: checkpointWithDocument.id,
                    custom_field_id: customFieldWithDocument.id,
                    new_value: {
                        type: SetFieldSupplierDocumentValue.type.SUPPLIER_DOCUMENT,
                        new_value: {
                            type: SetCustomFieldSupplierDocuments.type.SUPPLIER_DOCUMENTS,
                            supplier_documents: updatedDocuments.map(doc => ({
                                start_date: doc.start_date || '',
                                expiration_date: doc.expiration_date || '',
                                file_resource_id: doc.file_resource?.id,
                                status: doc.status
                            }))
                        }
                    }
                }));
            }
        }
    };

    const closeDocumentViewer = () => {
        dispatch(supplierDocumentsSliceActions.hideDocumentViewer());
    };

    useEffect(() => {
        dispatch(getAuditContentById({ auditId }));
        dispatch(getSupplierDocumentTypeGroups());
        dispatch(getProductionUnitDocumentTypeGroups());
        const intervalId = setInterval(() => dispatch(getAuditContentById({ auditId })), 5000);
        return () => {
            clearInterval(intervalId);
            dispatch(resetAuditWithContent());
        }
    }, [auditId]);

    useEffect(() => {
        setShowLockedAuditModal(auditWithContent?.audit?.locked);
    }, [auditWithContent]);

    if (errorCodeFetchingAudit) {
        return <NoAuditAccessPage />
    }

    if (auditWithContent?.submitted) {
        if (!!isTemporaryUserMode) {
            history.push('/audits/completed');
        } else {
            history.push('/audits/report/' + auditId);
        }
    }

    if (auditWithContent?.checklist?.headers?.length > 0 && !isLoading) {
        const { audit, checklist } = auditWithContent;
        return (
            <PageContainer headerClassName='' header={<div className='flex space-x-2 justify-between'>
                <DocumentTitle title={intl.formatMessage({ id: 'page_title.perform_audit' }, { name: audit.audit_type?.name })} />
                <div className='px-4 sm:px-0 text-3xl font-bold'>{audit.audit_type?.name}</div>
            </div>}>
                <div className='flex flex-wrap-reverse sm:flex-row space-x-0'>
                    <div className='sm:w-2/3 w-full sm:pr-6'>
                        {checklist.headers.map((header, index) => <AuditCheckpointHeader audit={audit} index={index} header={header} key={index} triedToSubmit={triedToSubmit} />)}
                        <div className='my-2'>
                            <div className='text-xl font-bold my-3 sm:px-0 px-4'><FormattedMessage id='web_based_audit.summary_and_submit' /></div>
                            <SummaryAndSubmit notify={() => {
                                setTriedToSubmit(true);
                                if (hasMandatoryInput) {
                                    setShowRequireInputModal(true);
                                }
                            }} checklist={checklist} />
                        </div>
                    </div>
                    <div className='sm:w-1/3 w-full mb-6 mt-2'>
                        <WebAuditInfo audit={audit} />
                        <AuditAttachments audit={audit} />
                    </div>
                    <ToTop />
                </div>

                <OpenInAppButton url={getAppLink({ auditId })} />

                {/* SupplierDocumentViewer is now here */}
                {documentation && auditWithContent?.audit && (
                    <SupplierDocumentViewer
                        linkedAudit={auditWithContent.audit}
                        linkedSupplier={{
                            id: auditWithContent.audit.target_production_unit?.id || auditWithContent.audit.target_supplier?.id || '',
                            name: auditWithContent.audit.target_production_unit?.name || auditWithContent.audit.target_supplier?.name || '',
                            number: auditWithContent.audit.target_production_unit?.number || auditWithContent.audit.target_supplier?.number || '',
                            type: auditWithContent.audit.target_production_unit ? 'production_unit' : 'supplier'
                        }}
                        actions={documentActions}
                        close={closeDocumentViewer}
                    />
                )}

                <Modal
                    size="small"
                    closeOnEscape
                    closeIcon
                    onClose={() => setShowRequireInputModal(false)}
                    open={showRequireInputModal}
                >
                    <Modal.Header>
                        <div className='flex flex-row justify-start font-bold'><FormattedMessage id='web_based_audit.mandatory_data_missing.title' /></div>
                    </Modal.Header>
                    <Modal.Content>
                        <div className='flex flex-row justify-start'><FormattedMessage id='web_based_audit.mandatory_data_missing.description' /></div>
                    </Modal.Content>
                    <Modal.Actions className="flex justify-end">
                        <Button primary onClick={() => {
                            setShowRequireInputModal(false)
                            scrollTo({
                                left: 0,
                                top: 0,
                                behavior: 'smooth'
                            });
                        }}>
                            <FormattedMessage id="web_based_audit.modal.confirm" />
                        </Button>
                    </Modal.Actions>
                </Modal>
                {showLockedAuditModal && <AuditLockedModal auditId={auditId} showContinueInBrowserGuide={showContinueInBrowserGuide} />}
            </PageContainer>
        );
    }

    return <Dimmer inverted active>
        <Loader className='dimLoader'><FormattedMessage id='globals.initialising_app.please_wait' /></Loader>
    </Dimmer>
}

export default PerformAuditPage;

export type DocumentationProductionUnitMap = {
    productionUnit: Record<string, boolean>;
    supplier: Record<string, boolean>;
};
export function buildDocumentationProductionUnitMap(checkpoints: AuditCheckpoint[]): DocumentationProductionUnitMap {
    const map: DocumentationProductionUnitMap = {
        productionUnit: {},
        supplier: {}
    };
    checkpoints?.forEach(checkpoint => {
        if (checkpoint.checkpoint_type == AuditCheckpoint.checkpoint_type.PRODUCTION_UNIT_CUSTOM_FIELDS) {
            checkpoint.production_unit_custom_fields?.forEach(customField => {
                if (customField.value.type == AuditCustomFieldSupplierDocumentValue.type.SUPPLIER_DOCUMENT) {
                    const existingActiveDocuments = customField.value.existing_documents?.filter(isDocumentValid);
                    const newDocuments = customField.value.new_documents;
                    existingActiveDocuments?.forEach(doc => {
                        map.productionUnit[doc.file_resource?.id] = true;
                    });
                    newDocuments?.forEach(doc => {
                        map.productionUnit[doc.file_resource?.id] = true;
                    });
                }
            });
        }

        if (checkpoint.checkpoint_type == AuditCheckpoint.checkpoint_type.SUPPLIER_CUSTOM_FIELDS) {
            checkpoint.supplier_custom_fields?.forEach(customField => {
                if (customField.value.type == AuditCustomFieldSupplierDocumentValue.type.SUPPLIER_DOCUMENT) {
                    const existingActiveDocuments = customField.value.existing_documents?.filter(isDocumentValid);
                    const newDocuments = customField.value.new_documents;
                    existingActiveDocuments?.forEach(doc => {
                        map.supplier[doc.file_resource?.id] = true;
                    });
                    newDocuments?.forEach(doc => {
                        map.supplier[doc.file_resource?.id] = true;
                    });
                }
            });
        }
    });
    return map;
}



function AuditCheckpointHeader(props: {
    audit: Audit,
    index: number,
    header: AuditChecklistHeader,
    triedToSubmit: boolean
}) {
    const { audit, header, triedToSubmit, index } = props;
    const requiresInput = doesChecklistHeaderHaveAnyMandatoryCheckpoints(header);
    const documentationProductionUnitMap = buildDocumentationProductionUnitMap(header.checkpoints);

    const [isOpen, setIsOpen] = React.useState(true);

    React.useEffect(() => {
        setIsOpen(triedToSubmit ? requiresInput : true)
    }, [triedToSubmit])

    return (
        <div className={`checkpoint-header mb-4 ${index == 0 ? '' : 'pt-4'}`}>
            <div className='flex flex-row justify-between items-center mb-3 cursor-pointer mx-2 sm:mx-0' onClick={() => setIsOpen(!isOpen)}>
                <div className='flex flex-row justify-start items-center'>
                    {
                        triedToSubmit && requiresInput &&
                        <Icon name='error' className='text-2xl text-status-rejected mr-1' />
                    }
                    <MultiLingualTextComponent text={header.title} className={`text-xl font-bold sm:px-0 px-2 w-fit`} />
                </div>
                <div className='flex flex-row justify-end items-center' >
                    <CheckpointHeaderProgressPopup header={header} />
                    <button type="button" title={isOpen ? "Collapse section" : "Expand section"} aria-label="Toggle section" aria-expaned={isOpen}>
                        {isOpen ? <Icon name='expand_less' className='text-2xl' /> : <Icon name='expand_more' className='text-2xl' />}
                    </button>
                </div>
            </div>
            {
                isOpen ? header.checkpoints.map((checkpoint, index) => <AuditCheckpointComponent audit={audit} checkpoint={checkpoint} key={index} triedToSubmit={triedToSubmit} documentationProductionUnitMap={documentationProductionUnitMap} />) : null
            }
        </div>
    )
}

function AuditCheckpointComponent(props: {
    audit: Audit,
    checkpoint: AuditCheckpoint,
    triedToSubmit: boolean,
    documentationProductionUnitMap: DocumentationProductionUnitMap
}) {
    const { audit, checkpoint, triedToSubmit, documentationProductionUnitMap } = props;
    const requiresInputOrMedia = doesCheckpointRequiresInputOrMedia(checkpoint);

    function getCheckpointComponent() {
        switch (checkpoint.checkpoint_type) {
            case AuditCheckpoint.checkpoint_type.TEXT_INPUTS:
                return <TextInputsCheckpoint audit={audit} checkpoint={checkpoint} triedToSubmit={triedToSubmit} />
            case AuditCheckpoint.checkpoint_type.TEXT_TABLE:
                return <TextTableCheckpoint audit={audit} checkpoint={checkpoint} triedToSubmit={triedToSubmit} />
            case AuditCheckpoint.checkpoint_type.MULTIPLE_CHOICE:
                return <MultipleChoiceCheckpoint audit={audit} checkpoint={checkpoint} triedToSubmit={triedToSubmit} />
            case AuditCheckpoint.checkpoint_type.PRODUCTION_UNIT_CUSTOM_FIELDS:
                return <ProductionUnitCustomFieldCheckpoint audit={audit} checkpoint={checkpoint} triedToSubmit={triedToSubmit} documentationProductionUnitMap={documentationProductionUnitMap} />
            case AuditCheckpoint.checkpoint_type.SUPPLIER_CUSTOM_FIELDS:
                return <SupplierCustomFieldCheckpoint audit={audit} checkpoint={checkpoint} triedToSubmit={triedToSubmit} documentationProductionUnitMap={documentationProductionUnitMap} />
            case AuditCheckpoint.checkpoint_type.MEASUREMENT_TABLES:
                return <MeasurementTableCheckpoint audit={audit} checkpoint={checkpoint} triedToSubmit={triedToSubmit} />
            default:
                return <DefaultCheckpoint audit={audit} checkpoint={checkpoint} triedToSubmit={triedToSubmit} />
        }
    }

    return <div className={`border bg-white py-4 px-4 rounded-md my-2 ${requiresInputOrMedia && triedToSubmit ? 'border-red-500' : ''}`}>
        {getCheckpointComponent()}
    </div>
}

type AppLinkConfig = {
    auditId: string,
    checkpointId?: string
}
export function getAppLink(config: AppLinkConfig): string {
    const { auditId, checkpointId } = config;
    const isTemporaryUser = !!sessionStorage.getItem('temporary_user_mode');
    const sessionId = sessionStorage.getItem('session_id');
    const params = isTemporaryUser && sessionId ? `session_id=${sessionId}&checkpoint_id=${checkpointId}` : `audit_id=${auditId}&checkpoint_id=${checkpointId}`;

    if (isAndroidOS()) {
        return `android-app://com.qarma.app/http/app.qarmainspect.com/q/app/applink/audits?${params}`;
    } else {
        return `https://app.qarmainspect.com/q/app/applink/audits?${params}`;
    }
}

export function triggerUniversalLink(url: string) {
    const link = document.createElement('a');
    link.href = url;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}

function AuditAttachments(props: {
    audit: Audit
}) {
    const { audit } = props;

    if (!audit.attachments || audit.attachments.length == 0) {
        return null;
    }
    return (
        <div className='border bg-white flex-col my-4'>
            <div className='text-lg font-bold px-4 py-4 border-b'><FormattedMessage id='attachments.header' /></div>
            {
                audit.attachments.map((attachment, index) => {
                    return (
                        <div className={`flex flex-row items-center my-2 mx-4`} key={index}>
                            <Icon className='text-grey text-xl pr-1' name='attachment' />
                            <div className='text-sm text-green-600 cursor-pointer' onClick={() => window.open(getFileResourceUrl(attachment), '_blank')}>{attachment.name}</div>
                        </div>
                    )
                })

            }
        </div>
    )
}

function OpenInAppButton(props: { url: string }): React.ReactElement {
    const { url } = props;
    const VISIBLE_AT_POSITION = 750;
    const intl = useIntl();
    const [scrollPosition, setScrollPosition] = React.useState({ x: 0, y: 0 });
    useScrollPosition(({ currPos }) => {
        setScrollPosition(currPos)
    }, []);

    return isMobileDevice() && (scrollPosition.y > -VISIBLE_AT_POSITION) &&
        <div className="fixed w-full flex-col justify-center xl:invisible px-8 bottom-0 bg-white py-8">
            <GoToAppButton appLink={url} text={intl.formatMessage({id: "web_based_audit.open_in_app"})} icon="open_in_new"/>
        </div>;
}

export function GoToAppButton(props: {
    className?: string,
    appLink: string, 
    text: string, 
    icon?: string
}) {
    const {appLink, text, icon, className} = props;
    return (
        <a href={appLink} className={twMerge('flex flex-row px-8 py-4 border-default rounded-md cursor-pointer border bg-branding-brand text-white justify-center text-base my-2 shrink-0 mr-4', className)}>
            <FormattedMessage id={text} />
            {icon && <Icon className="text-white pl-2" name={icon} />}
        </a>
    )
}

export function AuditCheckpointTitleAndActionMenu(props: {
    audit: Audit,
    checkpoint: AuditCheckpoint
}) {
    const { checkpoint } = props;
    const { audit } = useSelector(getAuditContentByIdSelector);
    const intl = useIntl();
    const hasComment = !!checkpoint.comment;
    const shouldAskForComment = checkpoint.checkpoint_status == AuditCheckpoint.checkpoint_status.PENDING || checkpoint.checkpoint_status == AuditCheckpoint.checkpoint_status.CHECKED_NOT_OK;
    const showCommentFieldByDefault = hasComment || shouldAskForComment;

    const [showingNoteInput, setShowingNoteInput] = React.useState(showCommentFieldByDefault);
    const [showingAddMediaModal, setShowingAddMediaModal] = React.useState(false);
    const [commentAutoFocus, setCommentAutoFocus] = React.useState(false);

    const dispatch = useAppDispatch();

    React.useEffect(() => {
        if (audit.locked) {
            setShowingAddMediaModal(false);
        }
    }, [audit])


    const [comment, setComment] = React.useState(checkpoint.comment);

    const addMediaContextMenuItem: ContextMenuActionItemType = {
        label: 'web_based_audit.buttons.add_media',
        icon: 'operation_take-photo',
        onPress: () => {
            setShowingAddMediaModal(true);
        }
    }
    const addNoteContextMenuItem: ContextMenuActionItemType = {
        label: 'web_based_audit.buttons.add_note',
        labelLeft: true,
        icon: 'object_comment',
        onPress: () => {
            setShowingNoteInput(true)
            setCommentAutoFocus(true);
        },
    }

    function doUpdateComment(comment: string) {
        const noUpdate = !comment && !checkpoint.comment;
        if (noUpdate) {
            return;
        } else {
            return dispatch(updateCheckpointComment({
                audit_id: audit.id,
                checkpoint_id: checkpoint.id,
                comment: comment
            }))
        }
    }

    return (
        <div>
            <div className='flex flex-row justify-between mb-4'>
                <div>
                    {checkpoint.checkpoint_type == AuditCheckpoint.checkpoint_type.PRODUCTION_UNIT_CUSTOM_FIELDS && <div className='text-sm text-gray-500'><FormattedMessage id='web_based_audit.production_unit_data' /></div>}
                    {checkpoint.checkpoint_type == AuditCheckpoint.checkpoint_type.SUPPLIER_CUSTOM_FIELDS && <div className='text-sm text-gray-500'><FormattedMessage id='web_based_audit.supplier_data' /></div>}

                    <MultiLingualTextComponent text={checkpoint.description} className={`text-lg font-bold w-fit`} />
                    <MultiLingualTextComponent text={checkpoint.instruction_details} className="text-sm w-fit" />
                    {
                        checkpoint.instruction_file_resources?.map((file, index) =>
                            <div className={`flex flex-row items-center ${index == checkpoint.instruction_file_resources.length - 1 ? 'mb-2' : ''}`} key={index}>
                                <Icon className='text-grey text-xl pr-1' name='attachment' />
                                <div className='text-sm text-green-600 cursor-pointer' onClick={() => window.open(getFileResourceUrl(file), '_blank')}>{file.name}</div>
                            </div>
                        )
                    }
                </div>
                <ContextMenu
                    className='h-fit-content gap-1px'
                    actions={
                        showingNoteInput ?
                            [addMediaContextMenuItem] :
                            [addNoteContextMenuItem, addMediaContextMenuItem]
                    }
                />
                <AddMediaModal appLink={getAppLink({ auditId: audit.id, checkpointId: checkpoint.id })} showing={showingAddMediaModal} setShowing={setShowingAddMediaModal} />
            </div>
            {
                showingNoteInput &&
                <TextArea
                    height={TextAreaHeight.SINGLE_LINE_AUTO_EXPAND}
                    value={comment}
                    onChange={setComment}
                    onBlur={doUpdateComment}
                    className="w-full sm:w-2/3 mb-4"
                    label='Note'
                    labelIcon='sticky_note_2'
                    placeholder={intl.formatMessage({ id: "web_based_audit.add_note.placeholder" })}
                    autoFocus={commentAutoFocus}
                />
            }
        </div>
    )
}

function getFileResourceUrl(file: FileResource) {
    switch (file.type) {
        case FileResource.type.FILE:
            return file.file.download_url;
        case FileResource.type.IMAGE:
            return file.image.image_url;
        case FileResource.type.VIDEO:
            return file.video.video_resources[0].url;
    }
}

export function MultiLingualTextComponent(props: {
    className: string
    text: MultiLingualText
}) {
    const intl = useIntl();
    const lang = getLocaleLanguageString(intl.locale);
    const { className, text } = props;
    const translatedText = getTextWithTranslation(text, lang);

    return translatedText ? <div className={twMerge(className, 'whitespace-pre-wrap')}>{translatedText}</div> : null
}

export function getTextWithTranslation(text: MultiLingualText, language: string): string {
    return text[language] || text.C;
}

export function isAuditImmutable(audit: Audit): boolean {
    return audit.locked || audit.status == AuditStatus.REPORT;
}